import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import useSEO from "../hooks/useSEO";
import { getBlogPostBySlug } from "../data/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getBlogPostBySlug(slug);

  // Add Article Schema for SEO
  useEffect(() => {
    if (post) {
      // Remove existing article schema if any
      const existingSchema = document.querySelector('script[data-seo="article-schema"]');
      if (existingSchema) {
        existingSchema.remove();
      }

      const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "description": post.excerpt,
        "image": post.image || "https://www.zsnexatech.com/assets/images/home-zs.svg",
        "datePublished": post.publishedAt,
        "dateModified": post.modifiedAt || post.publishedAt,
        "author": {
          "@type": "Organization",
          "name": "ZS NEXATECH",
          "url": "https://www.zsnexatech.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "ZS NEXATECH",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.zsnexatech.com/logo.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.zsnexatech.com/blog/${post.slug}`
        }
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'article-schema');
      script.textContent = JSON.stringify(articleSchema);
      document.head.appendChild(script);

      return () => {
        // Cleanup on unmount
        const schema = document.querySelector('script[data-seo="article-schema"]');
        if (schema) {
          schema.remove();
        }
      };
    }
  }, [post]);

  const seoTitle = post ? `${post.title} | ZS NEXATECH Blog` : "Post Not Found | ZS NEXATECH";
  const seoDescription = post ? post.excerpt : "The blog post you requested could not be found.";
  const seoKeywords = post ? `${post.category}, software engineering, product development` : "blog, engineering insights";
  const seoUrl = post ? `https://www.zsnexatech.com/blog/${post.slug}` : "https://www.zsnexatech.com/blog";

  useSEO(seoTitle, seoDescription, seoKeywords, seoUrl);

  if (!post) {

    return (
      <section className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-8 text-center">
        <h1 className="text-3xl font-bold text-white">Post not found</h1>
        <p className="mt-3 text-gray-100">This article may have moved or no longer exists.</p>
        <Link to="/blog" className="btn-gold mt-5 inline-flex">Go to blog</Link>
      </section>
    );
  }

  return (
    <div className="space-y-8">
      <section className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-8">
        <p className="text-xs tracking-widest font-semibold text-red-200">{post.category}</p>
        <h1 className="mt-2 text-4xl font-black text-white">{post.title}</h1>
        <p className="mt-3 text-gray-100">{post.excerpt}</p>
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-white/80">
          <span>{post.readTime}</span>
          <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
        </div>
      </section>

      <article className="premium-panel glass-panel animated-border space-y-4">
        {post.content.map((paragraph, index) => (
          <p key={index} className="text-gray-100 leading-relaxed">
            {paragraph}
          </p>
        ))}
      </article>

      <section className="flex flex-wrap gap-3">
        <Link to="/blog" className="link-gold">← Back to all insights</Link>
        <Link to="/book-consultation" className="btn-gold consultation-booking">Book Free Consultation</Link>
      </section>
    </div>
  );
}
